import { useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useRoleContext } from '../../../context/RoleContext';
import TableControls from '../../../components/common/TableControls';
import { fetchClubs } from '../../../services/clubService';
import { fetchOrganizations, fetchOrganizationsForAdmin } from '../../../services/organizationService';
import {
  deleteTeamAdmin,
  deleteTeamAdminForClub,
  fetchTeamAdmins,
  fetchTeamAdminsForCurrentClub,
  updateTeamAdmin,
  updateTeamAdminForClub,
} from '../../../services/teamAdminService';
import {
  unassignOrganizationDepartmentAdminRole,
  unassignOrganizationTournamentAdminRole,
  updateTournamentAdmin,
} from '../../../services/tournamentAdminService';
import {
  createTournament,
  deleteTournament,
  fetchTournamentAdmins,
  fetchTournaments,
  updateTournament,
  updateTournamentAdmins,
} from '../../../services/tournamentService';
import useTableControls from '../../../hooks/useTableControls';
import { getOrganizationId } from '../../../utils/organization';
import AssignRoleModal from '../../../ui/components/admin/AssignRoleModal';
import { normalizeAdminsRows, updateAdminsAfterRoleRemoval } from '../../common/utils/adminRoleState';

const SHOW_ARCHIVED_STORAGE_KEY = 'crickarena.organization-admin.show-archived';


const idsMatch = (leftId, rightId) => String(leftId || '') === String(rightId || '');


const OrganizationTeamAdminPage = () => {
  const { token, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { activeContext } = useRoleContext();
  const { organizationId: organizationIdFromRoute, tab: organizationTabBaseFromRoute, subTab: organizationSubTabFromRoute } = useParams();
  const userRoles = new Set([...(user?.roles || []), user?.role].filter(Boolean));
  const isOrganizationAdmin = userRoles.has('organization_admin');
  const isClubAdmin = userRoles.has('club_admin');
  const isTournamentAdmin = userRoles.has('tournament_admin');
  const isSuperAdmin = userRoles.has('super_admin');
  const showTournamentManagerColumns = !isOrganizationAdmin;
  const canManageTeamAdmins = isOrganizationAdmin || isClubAdmin || isSuperAdmin;

  const userOrganizationId = useMemo(() => getOrganizationId(user?.organization), [user?.organization]);

  const [teamAdmins, setTeamAdmins] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [status, setStatus] = useState({ message: '', tone: 'info' });
  const [tournamentStatus, setTournamentStatus] = useState({ message: '', tone: 'info' });
  const [isAssignAdminModalOpen, setIsAssignAdminModalOpen] = useState(false);
  const [assignAdminRole, setAssignAdminRole] = useState('');
  const [tournamentAdmins, setTournamentAdmins] = useState([]);
  const [managerCandidates, setManagerCandidates] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [showArchivedTournaments, setShowArchivedTournaments] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(SHOW_ARCHIVED_STORAGE_KEY) === 'true';
  });
  const [tournamentSelections, setTournamentSelections] = useState({});
  const [tournamentManagers, setTournamentManagers] = useState({});
  const [tournamentLoadError, setTournamentLoadError] = useState('');
  const [tournamentMessage, setTournamentMessage] = useState({ message: '', tone: 'info' });
  const [loadError, setLoadError] = useState('');
  const [isLoadingAdmins, setIsLoadingAdmins] = useState(true);
  const [isLoadingTournaments, setIsLoadingTournaments] = useState(true);
  const [newTournamentName, setNewTournamentName] = useState('');
  const [isCreatingTournament, setIsCreatingTournament] = useState(false);
  const [editingTournamentId, setEditingTournamentId] = useState('');
  const [deletingTournamentId, setDeletingTournamentId] = useState('');
  const [deletingTournamentAdminId, setDeletingTournamentAdminId] = useState('');
  const [editingTournamentAdminId, setEditingTournamentAdminId] = useState('');
  const [deletingTeamAdminId, setDeletingTeamAdminId] = useState('');
  const [editingTeamAdminId, setEditingTeamAdminId] = useState('');
  const [updatingTournamentId, setUpdatingTournamentId] = useState('');
  const [selectedOrganizationId, setSelectedOrganizationId] = useState(
    organizationIdFromRoute || userOrganizationId || '',
  );
  const [organizationTab, setOrganizationTab] = useState('tournaments');
  const [editModal, setEditModal] = useState({ type: '', id: '', name: '', email: '' });
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isSavingModal, setIsSavingModal] = useState(false);
  const [editModalError, setEditModalError] = useState('');
  const [deleteModal, setDeleteModal] = useState({
    type: '',
    id: '',
    title: '',
    message: '',
    isActive: true,
    roleToRemove: '',
    adminType: '',
  });

  useEffect(() => {
    if (organizationIdFromRoute && organizationIdFromRoute !== selectedOrganizationId) {
      setSelectedOrganizationId(organizationIdFromRoute);
    }
  }, [organizationIdFromRoute, selectedOrganizationId]);

  useEffect(() => {
    const validTabs = new Set(['tournaments', 'tournament-admins', 'department-admins']);
    const requestedTab = organizationSubTabFromRoute || organizationTabBaseFromRoute;
    const normalizedTab = validTabs.has(requestedTab) ? requestedTab : 'tournaments';
    if (organizationTab !== normalizedTab) {
      setOrganizationTab(normalizedTab);
    }
  }, [organizationSubTabFromRoute, organizationTab, organizationTabBaseFromRoute]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(SHOW_ARCHIVED_STORAGE_KEY, String(showArchivedTournaments));
    }
  }, [showArchivedTournaments]);

  useEffect(() => {
    if (userOrganizationId && !selectedOrganizationId) {
      setSelectedOrganizationId(userOrganizationId);
    }
  }, [selectedOrganizationId, userOrganizationId]);

  const showOrganizationTabs = isOrganizationAdmin || isSuperAdmin;

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setIsSavingModal(false);
    setEditModal({ type: '', id: '', name: '', email: '' });
    setEditModalError('');
  };

  const closeDeleteModal = () => {
    setDeleteModal({
      type: '',
      id: '',
      title: '',
      message: '',
      isActive: true,
      roleToRemove: '',
      adminType: '',
    });
  };

  const hasOrganizationAdminRole = (admin) => {
    const roles = new Set(admin?.roles || []);
    return roles.has('organization_admin');
  };

  const formatAdminRole = (role) => {
    switch (role) {
      case 'team_admin':
        return 'Department admin';
      case 'tournament_manager':
        return 'Tournament manager';
      case 'player_manager':
        return 'Player manager';
      case 'auction_manager':
        return 'Auction manager';
      case 'match_manager':
        return 'Match manager';
      default:
        return 'Tournament admin';
    }
  };

  const getManagerOptions = (_allowedRoles, selectedId) => {
    const options = tournamentAdminOptions;
    const selectedOption = tournamentAdminOptions.find(
      (admin) => (admin.id || admin._id) === selectedId
    );

    if (selectedOption && !options.some((admin) => (admin.id || admin._id) === selectedId)) {
      return [...options, selectedOption];
    }

    return options;
  };

  const tournamentSorters = useMemo(
    () => ({
      name: (first, second) => (first.name || '').localeCompare(second.name || '', undefined, { sensitivity: 'base' }),
      status: (first, second) => Number(first.isActive === false) - Number(second.isActive === false),
      admins: (first, second) => (first.admins?.length || 0) - (second.admins?.length || 0),
    }),
    [],
  );

  const tournamentAdminSorters = useMemo(
    () => ({
      name: (first, second) => (first.name || '').localeCompare(second.name || '', undefined, { sensitivity: 'base' }),
      email: (first, second) => (first.email || '').localeCompare(second.email || '', undefined, { sensitivity: 'base' }),
      managerRoles: (first, second) =>
        (first.managerRoles?.join(', ') || '').localeCompare(
          second.managerRoles?.join(', ') || '',
          undefined,
          { sensitivity: 'base' },
        ),
      createdAt: (first, second) => new Date(first.createdAt) - new Date(second.createdAt),
    }),
    [],
  );

  const teamAdminSorters = useMemo(
    () => ({
      name: (first, second) => (first.name || '').localeCompare(second.name || '', undefined, { sensitivity: 'base' }),
      email: (first, second) => (first.email || '').localeCompare(second.email || '', undefined, { sensitivity: 'base' }),
      createdAt: (first, second) => new Date(first.createdAt) - new Date(second.createdAt),
      affiliation: (first, second) =>
        (first.organization?.name || first.club?.name || '').localeCompare(
          second.organization?.name || second.club?.name || '',
          undefined,
          { sensitivity: 'base' },
        ),
    }),
    [],
  );

  const activeOrganizationTournaments = useMemo(
    () => tournaments.filter((tournament) => tournament.isActive !== false),
    [tournaments],
  );

  const archivedOrganizationTournaments = useMemo(
    () => tournaments.filter((tournament) => tournament.isActive === false),
    [tournaments],
  );

  const organizationTournamentsForView = showArchivedTournaments
    ? archivedOrganizationTournaments
    : activeOrganizationTournaments;

  const tournamentManagerColumnCount = showTournamentManagerColumns ? 3 : 0;
  const tournamentTableColumnCount = 4 + tournamentManagerColumnCount;

  const updateTournamentState = (tournamentsData) => {
    setTournaments(tournamentsData);
    setTournamentSelections(
      tournamentsData.reduce((acc, tournament) => {
        const id = tournament.id || tournament._id;
        const adminIds = (tournament.admins || []).map((admin) => admin.id || admin._id);
        acc[id] = isOrganizationAdmin ? adminIds.slice(0, 1) : adminIds;
        return acc;
      }, {}),
    );
    if (showTournamentManagerColumns) {
      setTournamentManagers(
        tournamentsData.reduce((acc, tournament) => {
          const id = tournament.id || tournament._id;
          acc[id] = {
            registrationManagerId:
              tournament.registrationManager?.id || tournament.registrationManager?._id || '',
            auctionManagerId:
              tournament.auctionManager?.id || tournament.auctionManager?._id || '',
            matchManagerId: tournament.matchManager?.id || tournament.matchManager?._id || '',
          };
          return acc;
        }, {}),
      );
    }
  };

  useEffect(() => {
    const loadData = async () => {
      if (!token) return;

      setLoadError('');
      setIsLoadingAdmins(canManageTeamAdmins);
      setTournamentLoadError('');
      setIsLoadingTournaments(isOrganizationAdmin || isTournamentAdmin || isSuperAdmin);

      if (canManageTeamAdmins) {
        try {
          const teamAdminResponse =
            isClubAdmin && !isOrganizationAdmin
              ? await fetchTeamAdminsForCurrentClub(token)
              : await fetchTeamAdmins(token);
          setTeamAdmins(teamAdminResponse.teamAdmins || []);

          if (isOrganizationAdmin && userOrganizationId) {
            const organizationResponse = await fetchOrganizations();
            setOrganizations(organizationResponse.organizations || []);
          }

          if (isSuperAdmin) {
            const organizationResponse = await fetchOrganizationsForAdmin(token);
            const availableOrganizations = organizationResponse.organizations || [];

            setOrganizations(availableOrganizations);

            if (!selectedOrganizationId && availableOrganizations.length === 1) {
              const onlyOrganization = availableOrganizations[0];
              setSelectedOrganizationId(onlyOrganization._id || onlyOrganization.id || '');
            }
          }

          if (isClubAdmin && user?.club) {
            const clubResponse = await fetchClubs();
            setClubs(clubResponse.clubs || []);
          }
        } catch (error) {
          setLoadError(error.message || 'Unable to load department admins.');
        } finally {
          setIsLoadingAdmins(false);
        }
      }

      if (isOrganizationAdmin || isTournamentAdmin || isSuperAdmin) {
        try {
          const organizationId = selectedOrganizationId || userOrganizationId;

          const requests = [fetchTournaments(token, organizationId)];
          if (isOrganizationAdmin || isSuperAdmin || isTournamentAdmin) {
            requests.push(fetchTournamentAdmins(token, organizationId, { includeUsers: true }));
          }

          const [tournamentResponse, tournamentAdminResponse] = await Promise.all(requests);

          const tournamentsData = tournamentResponse.tournaments || [];

          updateTournamentState(tournamentsData);
          if (tournamentAdminResponse) {
            setTournamentAdmins(normalizeAdminsRows(tournamentAdminResponse.admins || []));
            setManagerCandidates(tournamentAdminResponse.users || []);
          }
        } catch (error) {
          setTournamentLoadError(error.message || 'Unable to load tournaments.');
        } finally {
          setIsLoadingTournaments(false);
        }
      }
    };

    loadData();
  }, [
    canManageTeamAdmins,
    isClubAdmin,
    isOrganizationAdmin,
    isSuperAdmin,
    isTournamentAdmin,
    selectedOrganizationId,
    token,
    user?.club,
    userOrganizationId,
  ]);

  const {
    paginatedItems: visibleOrganizationTournaments,
    totalItems: totalOrganizationTournaments,
    totalPages: organizationTournamentPages,
    page: organizationTournamentPage,
    pageSize: organizationTournamentPageSize,
    sortKey: organizationTournamentSortKey,
    sortDirection: organizationTournamentSortDirection,
    setSortKey: setOrganizationTournamentSortKey,
    setSortDirection: setOrganizationTournamentSortDirection,
    setPage: setOrganizationTournamentPage,
    setPageSize: setOrganizationTournamentPageSize,
    searchTerm: organizationTournamentSearch,
    setSearchTerm: setOrganizationTournamentSearch,
    toggleDirection: toggleOrganizationTournamentDirection,
  } = useTableControls(organizationTournamentsForView, {
    initialSortKey: 'name',
    sorters: tournamentSorters,
    searchKeys: [
      'name',
      (tournament) => (tournament.isActive ? 'active' : 'archived'),
      (tournament) => (tournament.admins || []).map((admin) => admin.name || admin.email || '').join(' '),
    ],
  });

  const {
    paginatedItems: visibleTournamentAdmins,
    totalItems: totalTournamentAdmins,
    totalPages: tournamentAdminPages,
    page: tournamentAdminPage,
    pageSize: tournamentAdminPageSize,
    sortKey: tournamentAdminSortKey,
    sortDirection: tournamentAdminSortDirection,
    setSortKey: setTournamentAdminSortKey,
    setSortDirection: setTournamentAdminSortDirection,
    setPage: setTournamentAdminPage,
    setPageSize: setTournamentAdminPageSize,
    searchTerm: tournamentAdminSearch,
    setSearchTerm: setTournamentAdminSearch,
    toggleDirection: toggleTournamentAdminDirection,
  } = useTableControls(tournamentAdmins, {
    initialSortKey: 'createdAt',
    initialSortDirection: 'desc',
    sorters: tournamentAdminSorters,
    searchKeys: ['name', 'email', (admin) => (admin.managerRoles || []).join(' ')],
  });

  const {
    paginatedItems: visibleTournamentAssignments,
    totalItems: totalTournamentAssignments,
    totalPages: assignmentPages,
    page: assignmentPage,
    pageSize: assignmentPageSize,
    sortKey: assignmentSortKey,
    sortDirection: assignmentSortDirection,
    setSortKey: setAssignmentSortKey,
    setSortDirection: setAssignmentSortDirection,
    setPage: setAssignmentPage,
    setPageSize: setAssignmentPageSize,
    searchTerm: assignmentSearch,
    setSearchTerm: setAssignmentSearch,
    toggleDirection: toggleAssignmentDirection,
  } = useTableControls(tournaments, {
    initialSortKey: 'name',
    sorters: tournamentSorters,
    searchKeys: [
      'name',
      (tournament) => (tournament.isActive ? 'active' : 'archived'),
      (tournament) => (tournament.admins || []).map((admin) => admin.name || admin.email || '').join(' '),
    ],
  });

  const approvedTeamAdmins = teamAdmins.filter((admin) => admin.approvalStatus === 'approved');

  const {
    paginatedItems: visibleTeamAdmins,
    totalItems: totalTeamAdmins,
    totalPages: teamAdminPages,
    page: teamAdminPage,
    pageSize: teamAdminPageSize,
    sortKey: teamAdminSortKey,
    sortDirection: teamAdminSortDirection,
    setSortKey: setTeamAdminSortKey,
    setSortDirection: setTeamAdminSortDirection,
    setPage: setTeamAdminPage,
    setPageSize: setTeamAdminPageSize,
    searchTerm: teamAdminSearch,
    setSearchTerm: setTeamAdminSearch,
    toggleDirection: toggleTeamAdminDirection,
  } = useTableControls(approvedTeamAdmins, {
    initialSortKey: 'createdAt',
    initialSortDirection: 'desc',
    sorters: teamAdminSorters,
    searchKeys: [
      'name',
      'email',
      (admin) => admin.organization?.name || admin.club?.name || '',
    ],
  });

  const affiliationName = useMemo(() => {
    if (isOrganizationAdmin) {
      const targetId = userOrganizationId;
      const organization = organizations.find((item) => item._id === targetId || item.id === targetId);
      return organization?.name;
    }

    if (isClubAdmin) {
      const targetId = user?.club;
      const club = clubs.find((item) => item._id === targetId || item.id === targetId);
      return club?.name;
    }

    return null;
  }, [clubs, isClubAdmin, isOrganizationAdmin, organizations, user?.club, userOrganizationId]);

  const affiliationLabel = isOrganizationAdmin ? 'organization' : isClubAdmin ? 'club' : 'organization or club';

  const tournamentAdminOptions = useMemo(() => {
    const adminMap = new Map();
    const addAdminToMap = (admin) => {
      const adminId = admin?.id || admin?._id;

      if (!adminId) return;

      adminMap.set(adminId, {
        ...admin,
        id: adminId,
      });
    };

    [...managerCandidates, ...teamAdmins].forEach(addAdminToMap);

    tournaments.forEach((tournament) => {
      (tournament.admins || []).forEach(addAdminToMap);
      addAdminToMap(tournament.registrationManager);
      addAdminToMap(tournament.auctionManager);
      addAdminToMap(tournament.matchManager);
    });

    return Array.from(adminMap.values());
  }, [managerCandidates, teamAdmins, tournaments]);

  if (!token) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (!isOrganizationAdmin && !isClubAdmin && !isTournamentAdmin && !isSuperAdmin) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  const handleChange = (event) => {
    const { name, value } = event.target;
    setStatus({ message: '', tone: 'info' });
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    setStatus({
      message: 'Department admins cannot be created here. Assign an existing user by searching their email.',
      tone: 'danger',
    });
  };

  const deleteTeamAdminById = async (adminId) => {
    setDeletingTeamAdminId(adminId);
    setStatus({ message: '', tone: 'info' });

    try {
      if (isClubAdmin && !isOrganizationAdmin) {
        await deleteTeamAdminForClub(adminId, token);
      } else {
        await deleteTeamAdmin(adminId, token);
      }
      setTeamAdmins((prev) => prev.filter((item) => !idsMatch(item.id || item._id, adminId)));
      setStatus({ message: 'Department admin deleted successfully.', tone: 'success' });
    } catch (error) {
      setStatus({ message: error.message || 'Unable to delete department admin.', tone: 'danger' });
      throw error;
    } finally {
      setDeletingTeamAdminId('');
    }
  };

  const handleDeleteTeamAdmin = (adminId) => {
    const admin = teamAdmins.find((item) => idsMatch(item.id || item._id, adminId));

    if (!admin) return;

    setDeleteModal({
      type: 'teamAdmin',
      id: adminId,
      title: 'Delete department admin',
      message: `${admin.name || admin.email || 'This admin'} will be removed from department admin access.`,
      isActive: true,
      roleToRemove: 'department-admin',
      adminType: 'department',
    });
  };

  const handleEditTeamAdmin = (adminId) => {
    const admin = teamAdmins.find((item) => idsMatch(item.id || item._id, adminId));

    if (!admin) return;

    setEditModal({
      type: 'teamAdmin',
      id: adminId,
      name: admin.name || '',
      email: admin.email || '',
    });
    setEditModalError('');
    setIsEditModalOpen(true);
  };

  const deleteTournamentAdminById = async (adminId, roleToRemove = 'tournament-admin') => {
    setDeletingTournamentAdminId(adminId);
    setTournamentStatus({ message: '', tone: 'info' });

    try {
      const organizationId = selectedOrganizationId || userOrganizationId;
      const isDepartmentRole = roleToRemove === 'department-admin';
      if (isDepartmentRole) {
        const response = await unassignOrganizationDepartmentAdminRole(organizationId, adminId, token);
        const removedRole = response?.removed === 'department_admin' ? 'department-admin' : roleToRemove;
        setTournamentAdmins((prev) =>
          updateAdminsAfterRoleRemoval(prev, {
            adminId,
            userId: response?.userId,
            roleToRemove: removedRole,
          }),
        );
      } else {
        const response = await unassignOrganizationTournamentAdminRole(organizationId, adminId, token);
        const removedRole = response?.removed === 'department_admin' ? 'department-admin' : 'tournament-admin';
        setTournamentAdmins((prev) =>
          updateAdminsAfterRoleRemoval(prev, {
            adminId,
            userId: response?.userId,
            roleToRemove: removedRole,
          }),
        );
      }
      setTournamentStatus({
        message: `${isDepartmentRole ? 'Department' : 'Tournament'} admin role removed successfully.`,
        tone: 'success',
      });
    } catch (error) {
      setTournamentStatus({
        message:
          error.message
          || `Unable to remove ${roleToRemove === 'department-admin' ? 'department' : 'tournament'} admin role.`,
        tone: 'danger',
      });
      throw error;
    } finally {
      setDeletingTournamentAdminId('');
    }
  };

  const handleDeleteTournamentAdmin = (adminId, roleToRemove = 'tournament-admin') => {
    const admin = tournamentAdmins.find((item) => idsMatch(item.id || item._id, adminId));

    if (!admin) return;

    const isDepartmentRole = roleToRemove === 'department-admin';
    setDeleteModal({
      type: 'tournamentAdmin',
      id: adminId,
      title: isDepartmentRole ? 'Remove Department Admin' : 'Remove Tournament Admin',
      message: `${admin.name || admin.email || 'This user'} will lose ${isDepartmentRole ? 'Department Admin' : 'Tournament Admin'} access.`,
      isActive: true,
      roleToRemove,
      adminType: isDepartmentRole ? 'department' : 'tournament',
    });
  };

  const handleEditTournamentAdmin = (adminId) => {
    const admin = tournamentAdmins.find((item) => idsMatch(item.id || item._id, adminId));

    if (!admin) return;

    setEditModal({
      type: 'tournamentAdmin',
      id: adminId,
      name: admin.name || '',
      email: admin.email || '',
    });
    setEditModalError('');
    setIsEditModalOpen(true);
  };

  const handleTournamentNameChange = (event) => {
    setTournamentMessage({ message: '', tone: 'info' });
    setNewTournamentName(event.target.value);
  };

  const handleCreateTournament = async (event) => {
    event.preventDefault();

    if (!newTournamentName.trim()) {
      setTournamentMessage({ message: 'Please provide a tournament name.', tone: 'danger' });
      return;
    }

    if (isSuperAdmin && !selectedOrganizationId) {
      setTournamentMessage({ message: 'Please select an organization first.', tone: 'danger' });
      return;
    }

    setIsCreatingTournament(true);
    setTournamentMessage({ message: '', tone: 'info' });

    try {
      const organizationId = selectedOrganizationId || userOrganizationId;
      const response = await createTournament(
        {
          name: newTournamentName.trim(),
          organizationId,
        },
        token,
      );
      const tournament = response.tournament;

      if (tournament) {
        const tournamentId = tournament.id || tournament._id;

        setTournaments((prev) => [tournament, ...prev.filter((item) => (item.id || item._id) !== tournamentId)]);
        setTournamentSelections((prev) => ({ ...prev, [tournamentId]: prev[tournamentId] || [] }));
        setTournamentManagers((prev) => ({
          ...prev,
          [tournamentId]: prev[tournamentId] || {
            registrationManagerId: '',
            auctionManagerId: '',
            matchManagerId: '',
          },
        }));
      }

      const tournamentResponse = await fetchTournaments(token, selectedOrganizationId || userOrganizationId);
      updateTournamentState(tournamentResponse.tournaments || []);
      setTournamentMessage({ message: 'Tournament created successfully.', tone: 'success' });
      setNewTournamentName('');
    } catch (error) {
      setTournamentMessage({ message: error.message || 'Unable to create tournament.', tone: 'danger' });
    } finally {
      setIsCreatingTournament(false);
    }
  };

  const handleEditTournament = (tournament) => {
    const tournamentId = tournament.id || tournament._id;

    if (!tournamentId) return;

    setEditModal({
      type: 'tournament',
      id: tournamentId,
      name: tournament.name || '',
      email: '',
    });
    setEditModalError('');
    setIsEditModalOpen(true);
  };

  const handleEditModalSubmit = async (event) => {
    event.preventDefault();

    if (!editModal.type || !editModal.id) {
      closeEditModal();
      return;
    }

    setIsSavingModal(true);
    setEditModalError('');

    if (editModal.type === 'teamAdmin') {
      const admin = teamAdmins.find((item) => idsMatch(item.id || item._id, editModal.id));

      if (!admin) {
        closeEditModal();
        setIsSavingModal(false);
        return;
      }

      const trimmedName = editModal.name.trim();
      const trimmedEmail = editModal.email.trim();
      const payload = {};

      if (!trimmedName || !trimmedEmail) {
        setEditModalError('Name and email are required.');
        setIsSavingModal(false);
        return;
      }

      if (trimmedName !== admin.name) {
        payload.name = trimmedName;
      }

      if (trimmedEmail !== admin.email) {
        payload.email = trimmedEmail;
      }

      if (Object.keys(payload).length === 0) {
        setEditModalError('No changes to save.');
        setIsSavingModal(false);
        return;
      }

      setEditingTeamAdminId(editModal.id);
      setStatus({ message: '', tone: 'info' });

      try {
        const response =
          isClubAdmin && !isOrganizationAdmin
            ? await updateTeamAdminForClub(editModal.id, payload, token)
            : await updateTeamAdmin(editModal.id, payload, token);
        setTeamAdmins((prev) => prev.map((item) => (idsMatch(item.id || item._id, editModal.id) ? response.teamAdmin : item)));
        setStatus({ message: 'Department admin updated successfully.', tone: 'success' });
        closeEditModal();
      } catch (error) {
        setStatus({ message: error.message || 'Unable to update department admin.', tone: 'danger' });
        setEditModalError(error.message || 'Unable to update department admin.');
      } finally {
        setEditingTeamAdminId('');
      }
    } else if (editModal.type === 'tournamentAdmin') {
      const admin = tournamentAdmins.find((item) => idsMatch(item.id || item._id, editModal.id));

      if (!admin) {
        closeEditModal();
        setIsSavingModal(false);
        return;
      }

      const trimmedName = editModal.name.trim();
      const trimmedEmail = editModal.email.trim();
      const payload = {};

      if (!trimmedName || !trimmedEmail) {
        setEditModalError('Name and email are required.');
        setIsSavingModal(false);
        return;
      }

      if (trimmedName !== admin.name) {
        payload.name = trimmedName;
      }

      if (trimmedEmail !== admin.email) {
        payload.email = trimmedEmail;
      }

      if (Object.keys(payload).length === 0) {
        setEditModalError('No changes to save.');
        setIsSavingModal(false);
        return;
      }

      setEditingTournamentAdminId(editModal.id);
      setTournamentStatus({ message: '', tone: 'info' });

      try {
        const response = await updateTournamentAdmin(editModal.id, payload, token);
        setTournamentAdmins((prev) => normalizeAdminsRows(prev.map((item) => (idsMatch(item.id || item._id, editModal.id) ? response.admin : item))));
        setTournamentStatus({ message: 'Tournament admin updated successfully.', tone: 'success' });
        closeEditModal();
      } catch (error) {
        setTournamentStatus({ message: error.message || 'Unable to update tournament admin.', tone: 'danger' });
        setEditModalError(error.message || 'Unable to update tournament admin.');
      } finally {
        setEditingTournamentAdminId('');
      }
    } else if (editModal.type === 'tournament') {
      const tournament = tournaments.find((item) => idsMatch(item.id || item._id, editModal.id));

      if (!tournament) {
        closeEditModal();
        setIsSavingModal(false);
        return;
      }

      if (isSuperAdmin && !selectedOrganizationId) {
        setEditModalError('Select an organization to edit tournaments.');
        setIsSavingModal(false);
        return;
      }

      const trimmedName = editModal.name.trim();

      if (!trimmedName) {
        setEditModalError('Tournament name is required.');
        setIsSavingModal(false);
        return;
      }

      if (trimmedName === tournament.name) {
        setEditModalError('No changes to save.');
        setIsSavingModal(false);
        return;
      }

      const payload = { name: trimmedName };

      if (isSuperAdmin) {
        payload.organizationId = selectedOrganizationId;
      }

      setEditingTournamentId(editModal.id);
      setTournamentMessage({ message: '', tone: 'info' });

      try {
        const response = await updateTournament(editModal.id, payload, token);
        const updatedTournaments = tournaments.map((item) =>
          idsMatch(item.id || item._id, editModal.id) ? response.tournament : item
        );

        updateTournamentState(updatedTournaments);
        setTournamentMessage({ message: 'Tournament updated successfully.', tone: 'success' });
        closeEditModal();
      } catch (error) {
        setTournamentMessage({ message: error.message || 'Unable to update tournament.', tone: 'danger' });
        setEditModalError(error.message || 'Unable to update tournament.');
      } finally {
        setEditingTournamentId('');
      }
    }

    setIsSavingModal(false);
  };

  const handleTournamentAdminSelectionChange = (tournamentId, selection, allowMultiple) => {
    const selectedAdmins = allowMultiple
      ? Array.from(selection).map((option) => option.value)
      : selection
        ? [selection]
        : [];
    setTournamentSelections((prev) => ({ ...prev, [tournamentId]: selectedAdmins }));
  };

  const handleTournamentManagerChange = (tournamentId, field, value) => {
    setTournamentManagers((prev) => ({
      ...prev,
      [tournamentId]: {
        ...(prev[tournamentId] || {}),
        [field]: value,
      },
    }));
  };

  const handleSaveTournamentAdmins = async (tournamentId) => {
    const adminIds = tournamentSelections[tournamentId] || [];
    const normalizedAdminIds = isOrganizationAdmin ? adminIds.slice(0, 1) : adminIds;

    setUpdatingTournamentId(tournamentId);
    setTournamentMessage({ message: '', tone: 'info' });

    try {
      const managerPayload = showTournamentManagerColumns ? tournamentManagers[tournamentId] : {};
      const scopePayload = isSuperAdmin ? { organizationId: selectedOrganizationId } : {};
      const response = await updateTournamentAdmins(
        tournamentId,
        normalizedAdminIds,
        token,
        managerPayload,
        scopePayload,
      );
      const updatedTournament = response.tournament;
      const updatedId = updatedTournament.id || updatedTournament._id;

      setTournaments((prev) =>
        prev.map((tournament) =>
          (tournament.id === updatedId || tournament._id === updatedId) ? updatedTournament : tournament,
        ),
      );
      setTournamentSelections((prev) => ({
        ...prev,
        [updatedId]: (updatedTournament.admins || []).map((admin) => admin.id || admin._id),
      }));
      setTournamentManagers((prev) => ({
        ...prev,
        [updatedId]: showTournamentManagerColumns
          ? {
              registrationManagerId:
                updatedTournament.registrationManager?.id || updatedTournament.registrationManager?._id || '',
              auctionManagerId:
                updatedTournament.auctionManager?.id || updatedTournament.auctionManager?._id || '',
              matchManagerId:
                updatedTournament.matchManager?.id || updatedTournament.matchManager?._id || '',
            }
          : {},
      }));

      setTournamentMessage({ message: 'Tournament admins updated successfully.', tone: 'success' });
    } catch (error) {
      setTournamentMessage({ message: error.message || 'Unable to update tournament admins.', tone: 'danger' });
    } finally {
      setUpdatingTournamentId('');
    }
  };

  const deleteTournamentById = async (tournamentId) => {
    const tournament = tournaments.find((item) => item.id === tournamentId || item._id === tournamentId);

    if (!tournament) return;

    const isActive = tournament.isActive !== false;

    setDeletingTournamentId(tournamentId);
    setTournamentMessage({ message: '', tone: 'info' });

    try {
      const response = await deleteTournament(tournamentId, token);
      const updatedTournament = response.tournament;
      const updatedId = updatedTournament?.id || updatedTournament?._id;

      if (updatedId) {
        setTournaments((prev) =>
          prev.map((item) => (item.id === updatedId || item._id === updatedId ? updatedTournament : item)),
        );
      } else {
        setTournaments((prev) => prev.filter((item) => item.id !== tournamentId && item._id !== tournamentId));
      }

      const successMessage =
        response.message || (isActive ? 'Tournament archived successfully.' : 'Tournament removed from archive.');

      setTournamentMessage({ message: successMessage, tone: 'success' });
    } catch (error) {
      setTournamentMessage({ message: error.message || 'Unable to delete tournament.', tone: 'danger' });
      throw error;
    } finally {
      setDeletingTournamentId('');
    }
  };

  const handleDeleteTournament = (tournamentId) => {
    const tournament = tournaments.find((item) => item.id === tournamentId || item._id === tournamentId);

    if (!tournament) return;

    const isActive = tournament.isActive !== false;

    setDeleteModal({
      type: 'tournament',
      id: tournamentId,
      title: isActive ? 'Archive tournament' : 'Delete tournament',
      message: isActive
        ? `${tournament.name} will be archived. Assigned admins will be retained for your records.`
        : `${tournament.name} is archived and will be permanently removed.`,
      isActive,
    });
  };

  const confirmDeleteModal = async () => {
    if (!deleteModal.id || !deleteModal.type) return;

    try {
      if (deleteModal.type === 'teamAdmin') {
        await deleteTeamAdminById(deleteModal.id);
      } else if (deleteModal.type === 'tournamentAdmin') {
        await deleteTournamentAdminById(deleteModal.id, deleteModal.roleToRemove || 'tournament-admin');
      } else if (deleteModal.type === 'tournament') {
        await deleteTournamentById(deleteModal.id);
      }
      closeDeleteModal();
    } catch {
      // Errors are handled by the individual delete methods through status messages.
    }
  };

  const isDeleteActionInProgress =
    (deleteModal.type === 'teamAdmin' && deletingTeamAdminId === deleteModal.id) ||
    (deleteModal.type === 'tournamentAdmin' && deletingTournamentAdminId === deleteModal.id) ||
    (deleteModal.type === 'tournament' && deletingTournamentId === deleteModal.id);

  const deleteModalConfirmLabel = (() => {
    if (deleteModal.type === 'teamAdmin') {
      return deletingTeamAdminId === deleteModal.id ? 'Removing...' : 'Delete department admin';
    }

    if (deleteModal.type === 'tournamentAdmin') {
      if (deletingTournamentAdminId === deleteModal.id) return 'Removing...';
      return deleteModal.roleToRemove === 'department-admin'
        ? 'Remove Department Admin'
        : 'Remove Tournament Admin';
    }

    if (deleteModal.type === 'tournament') {
      if (deleteModal.isActive) {
        return deletingTournamentId === deleteModal.id ? 'Archiving...' : 'Archive tournament';
      }

      return deletingTournamentId === deleteModal.id ? 'Deleting...' : 'Delete tournament';
    }

    return 'Delete';
  })();

  return (
    <>
      <div className="card shadow-sm border-0">
        <div className="card-body d-grid gap-4">
          {isSuperAdmin && (
            <div className="alert alert-info py-2 mb-0">
              You are viewing organization and tournament admin tooling as a super admin. Actions are available, but
              some workflows may require selecting an organization or club before they take effect.
            </div>
          )}

          {activeContext?.type === 'organization_admin' && (
            <div className="alert alert-secondary py-2 mb-0">
              Organization: <strong>{activeContext?.entityName || activeContext?.entityId || 'Unknown'}</strong>
            </div>
          )}

        {showOrganizationTabs && !isSuperAdmin && (
          <div className="d-flex flex-wrap gap-2">
            {[
              { key: 'tournaments', label: 'Tournaments' },
              { key: 'tournament-admins', label: 'Tournament admins' },
              { key: 'department-admins', label: 'Department admins' },
            ].map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={`btn ${organizationTab === tab.key ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => {
                  setOrganizationTab(tab.key);
                  if (selectedOrganizationId) {
                    const nextPath = tab.key === 'tournaments'
                      ? `/dashboard/organization/${selectedOrganizationId}/tournaments`
                      : `/dashboard/organization/${selectedOrganizationId}/tournaments/${tab.key}`;
                    navigate(nextPath);
                  }
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}

        {(!showOrganizationTabs || organizationTab === 'tournaments') && (isOrganizationAdmin || isSuperAdmin) && (
          <div className="card border-0 shadow-sm">
            <div className="card-body d-grid gap-3">
              <div className="d-flex flex-column flex-sm-row align-items-sm-center justify-content-sm-between">
                <div>
                  <p className="eyebrow text-primary mb-1">Tournaments</p>
                  <h2 className="h5 mb-2">
                    {showArchivedTournaments ? 'Archived tournaments' : 'Create and manage tournaments'}
                  </h2>
                  <p className="text-muted mb-0">
                    {showArchivedTournaments
                      ? 'Review archived tournaments for your organization and remove entries you no longer need.'
                      : 'Add tournaments for your organization and assign multiple admins to help manage them.'}
                  </p>
                </div>
                <div className="d-flex align-items-start gap-2">
                  <div className="d-flex flex-column text-end">
                    <span className="fw-semibold">{activeOrganizationTournaments.length} active</span>
                    <span className="text-muted small">{archivedOrganizationTournaments.length} archived</span>
                  </div>
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={() => setShowArchivedTournaments((prev) => !prev)}
                  >
                    {showArchivedTournaments ? 'Back to active' : 'View archived tournaments'}
                  </button>
                </div>
              </div>

              {!showArchivedTournaments && (
                <form className="d-grid gap-3" onSubmit={handleCreateTournament}>
                  {isSuperAdmin && (
                    <div className="row g-3">
                      <div className="col-lg-6 col-xl-4">
                        <label className="form-label" htmlFor="tournamentOrganization">
                          Organization
                        </label>
                        <select
                          id="tournamentOrganization"
                          className="form-select"
                          value={selectedOrganizationId}
                          onChange={(event) => setSelectedOrganizationId(event.target.value)}
                        >
                          <option value="">Select an organization</option>
                          {organizations.map((organization) => {
                            const organizationId = organization.id || organization._id;

                            return (
                              <option key={organizationId} value={organizationId}>
                                {organization.name || 'Unnamed organization'}
                              </option>
                            );
                          })}
                        </select>
                        <p className="text-muted small mb-0">
                          Required when creating tournaments or tournament admins as a super admin.
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="row g-3 align-items-end">
                    <div className="col-lg-6">
                      <label className="form-label" htmlFor="tournamentName">
                        Tournament name
                      </label>
                      <input
                        id="tournamentName"
                        name="tournamentName"
                        type="text"
                        className="form-control"
                        placeholder="Spring Invitational"
                        value={newTournamentName}
                        onChange={handleTournamentNameChange}
                      />
                    </div>
                    <div className="col-lg-6 d-flex flex-column flex-sm-row gap-2">
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={isCreatingTournament || (isSuperAdmin && !selectedOrganizationId)}
                      >
                        {isCreatingTournament ? 'Creating tournament...' : 'Create tournament'}
                      </button>
                      <p className="mb-0 text-muted small">
                        Tournaments remain editable until archived.
                      </p>
                    </div>
                  </div>

                  {tournamentMessage.message && (
                    <div
                      className={`alert alert-${
                        tournamentMessage.tone === 'success' ? 'success' : 'danger'
                      } py-2 mb-0`}
                    >
                      {tournamentMessage.message}
                    </div>
                  )}
                </form>
              )}

                {tournamentLoadError && <div className="alert alert-danger py-2 mb-0">{tournamentLoadError}</div>}

                <TableControls
                  sortKey={organizationTournamentSortKey}
                  sortDirection={organizationTournamentSortDirection}
                  onSortKeyChange={setOrganizationTournamentSortKey}
                  onSortDirectionChange={(direction) =>
                    direction
                      ? setOrganizationTournamentSortDirection(direction)
                      : toggleOrganizationTournamentDirection()
                  }
                  sortOptions={[
                    { value: 'name', label: 'Name' },
                    { value: 'admins', label: 'Admin count' },
                    { value: 'status', label: 'Status' },
                  ]}
                  page={organizationTournamentPage}
                  totalPages={organizationTournamentPages}
                  totalItems={totalOrganizationTournaments}
                  pageSize={organizationTournamentPageSize}
                  onPageChange={setOrganizationTournamentPage}
                  onPageSizeChange={setOrganizationTournamentPageSize}
                  searchTerm={organizationTournamentSearch}
                  onSearchChange={setOrganizationTournamentSearch}
                  searchPlaceholder={`Search ${showArchivedTournaments ? 'archived' : 'active'} tournaments or admins`}
                />

                <div className="table-responsive">
                  <table className="table align-middle mb-0">
                    <thead>
                      <tr>
                        <th scope="col">Tournament</th>
                        <th scope="col">Admins</th>
                        {showTournamentManagerColumns && (
                          <>
                            <th scope="col">Player manager</th>
                            <th scope="col">Auction manager</th>
                            <th scope="col">Match manager</th>
                          </>
                        )}
                        <th scope="col">Status</th>
                        <th scope="col" className="text-end">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {isLoadingTournaments && (
                        <tr>
                          <td colSpan={tournamentTableColumnCount} className="text-center text-muted">
                            Loading tournaments...
                          </td>
                        </tr>
                      )}
                      {!isLoadingTournaments && totalOrganizationTournaments === 0 && (
                        <tr>
                          <td colSpan={tournamentTableColumnCount} className="text-center text-muted">
                            {showArchivedTournaments
                              ? 'No archived tournaments to review yet.'
                              : 'No tournaments match your filters yet.'}
                          </td>
                        </tr>
                      )}
                      {!isLoadingTournaments &&
                        visibleOrganizationTournaments.map((tournament) => {
                          const tournamentId = tournament.id || tournament._id;
                          const adminSelection = tournamentSelections[tournamentId] || [];
                          const allowMultipleAdmins = !isOrganizationAdmin;
                          const managerSelection = tournamentManagers[tournamentId] || {};
                          const isActiveTournament = tournament.isActive !== false;

                          return (
                            <tr key={tournamentId}>
                              <td className="fw-semibold">{tournament.name}</td>
                              <td style={{ minWidth: '240px' }}>
                                <select
                                  multiple={allowMultipleAdmins}
                                  className="form-select"
                                  value={allowMultipleAdmins ? adminSelection : adminSelection[0] || ''}
                                  onChange={(event) =>
                                    handleTournamentAdminSelectionChange(
                                      tournamentId,
                                      allowMultipleAdmins
                                        ? event.target.selectedOptions
                                        : event.target.value,
                                      allowMultipleAdmins,
                                    )
                                  }
                                  disabled={!tournament.isActive || updatingTournamentId === tournamentId}
                                >
                                  {tournamentAdminOptions.map((admin) => (
                                    <option key={admin.id} value={admin.id}>
                                      {admin.name} ({admin.email}) · {formatAdminRole(admin.role)}
                                    </option>
                                  ))}
                                </select>
                                {allowMultipleAdmins && (
                                  <p className="small text-muted mb-0 mt-1">
                                    Hold Ctrl/Command to select multiple admins.
                                  </p>
                                )}
                              </td>
                              {showTournamentManagerColumns && (
                                <>
                                  <td>
                                    <select
                                      className="form-select"
                                      value={managerSelection.registrationManagerId || ''}
                                      onChange={(event) =>
                                        handleTournamentManagerChange(
                                          tournamentId,
                                          'registrationManagerId',
                                          event.target.value,
                                        )
                                      }
                                      disabled={!tournament.isActive || updatingTournamentId === tournamentId}
                                    >
                                      <option value="">No manager selected</option>
                                      {getManagerOptions(
                                        ['player_manager', 'tournament_manager'],
                                        managerSelection.registrationManagerId,
                                      ).map((admin) => (
                                        <option key={admin.id} value={admin.id}>
                                          {admin.name} ({admin.email})
                                        </option>
                                      ))}
                                    </select>
                                  </td>
                                  <td>
                                    <select
                                      className="form-select"
                                      value={managerSelection.auctionManagerId || ''}
                                      onChange={(event) =>
                                        handleTournamentManagerChange(
                                          tournamentId,
                                          'auctionManagerId',
                                          event.target.value,
                                        )
                                      }
                                      disabled={!tournament.isActive || updatingTournamentId === tournamentId}
                                    >
                                      <option value="">No manager selected</option>
                                      {getManagerOptions(
                                        ['auction_manager', 'tournament_manager'],
                                        managerSelection.auctionManagerId,
                                      ).map((admin) => (
                                        <option key={admin.id} value={admin.id}>
                                          {admin.name} ({admin.email})
                                        </option>
                                      ))}
                                    </select>
                                  </td>
                                  <td>
                                    <select
                                      className="form-select"
                                      value={managerSelection.matchManagerId || ''}
                                      onChange={(event) =>
                                        handleTournamentManagerChange(
                                          tournamentId,
                                          'matchManagerId',
                                          event.target.value,
                                        )
                                      }
                                      disabled={!tournament.isActive || updatingTournamentId === tournamentId}
                                    >
                                      <option value="">No manager selected</option>
                                      {getManagerOptions(
                                        ['match_manager', 'tournament_manager'],
                                        managerSelection.matchManagerId,
                                      ).map((admin) => (
                                        <option key={admin.id} value={admin.id}>
                                          {admin.name} ({admin.email})
                                        </option>
                                      ))}
                                    </select>
                                  </td>
                                </>
                              )}
                              <td>
                                <span
                                  className={`badge ${
                                    isActiveTournament
                                      ? 'bg-success-subtle text-success-emphasis'
                                      : 'bg-secondary-subtle text-secondary-emphasis'
                                  }`}
                                >
                                  {isActiveTournament ? 'Active' : 'Archived'}
                                </span>
                              </td>
                              <td className="text-end">
                                <div className="d-flex flex-column flex-sm-row gap-2 justify-content-end">
                                  <button
                                    type="button"
                                    className="btn btn-outline-secondary btn-sm"
                                    onClick={() => handleEditTournament(tournament)}
                                    disabled={
                                      editingTournamentId === tournamentId ||
                                      deletingTournamentId === tournamentId ||
                                      updatingTournamentId === tournamentId
                                    }
                                  >
                                    {editingTournamentId === tournamentId ? 'Updating...' : 'Edit'}
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-outline-primary btn-sm"
                                    onClick={() => handleSaveTournamentAdmins(tournamentId)}
                                    disabled={updatingTournamentId === tournamentId || !isActiveTournament}
                                  >
                                    {updatingTournamentId === tournamentId
                                      ? 'Saving...'
                                      : showTournamentManagerColumns
                                        ? 'Save managers'
                                        : 'Save admins'}
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-outline-danger btn-sm"
                                    onClick={() => handleDeleteTournament(tournamentId)}
                                    disabled={deletingTournamentId === tournamentId}
                                  >
                                    {deletingTournamentId === tournamentId
                                      ? 'Deleting...'
                                      : isActiveTournament
                                        ? 'Archive'
                                        : 'Delete from archive'}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

        {(!showOrganizationTabs || organizationTab === 'tournament-admins') &&
          (isOrganizationAdmin || isSuperAdmin || isTournamentAdmin) && (
          <div className="card border-0 shadow-sm">
            <div className="card-body d-grid gap-3">
              <div className="d-flex flex-column flex-sm-row align-items-sm-center justify-content-sm-between">
                <div>
                  <p className="eyebrow text-primary mb-1">Tournament admins</p>
                  <h2 className="h5 mb-2">Assign tournament admins for your organization</h2>
                  <p className="text-muted mb-0">
                    Tournament administrators are assigned from existing users by searching email.
                  </p>
                </div>
                <span className="badge bg-primary-subtle text-primary-emphasis">
                  {isOrganizationAdmin || isSuperAdmin ? 'Organization admin view' : 'Tournament admin view'}
                </span>
              </div>

              <div className="d-flex flex-column flex-sm-row gap-2 align-items-start">
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={isSuperAdmin && !selectedOrganizationId}
                    onClick={() => {
                      setAssignAdminRole('tournament_admin');
                      setIsAssignAdminModalOpen(true);
                    }}
                  >
                    Assign Tournament Admin
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    disabled={isSuperAdmin && !selectedOrganizationId}
                    onClick={() => {
                      setAssignAdminRole('department_admin');
                      setIsAssignAdminModalOpen(true);
                    }}
                  >
                    Assign Department Admin
                  </button>
                </div>

              <TableControls
                sortKey={tournamentAdminSortKey}
                sortDirection={tournamentAdminSortDirection}
                onSortKeyChange={setTournamentAdminSortKey}
                onSortDirectionChange={(direction) =>
                  direction ? setTournamentAdminSortDirection(direction) : toggleTournamentAdminDirection()
                }
                sortOptions={[
                  { value: 'createdAt', label: 'Created' },
                  { value: 'name', label: 'Name' },
                  { value: 'email', label: 'Email' },
                  { value: 'managerRoles', label: 'Manager roles' },
                ]}
                page={tournamentAdminPage}
                totalPages={tournamentAdminPages}
                totalItems={totalTournamentAdmins}
                pageSize={tournamentAdminPageSize}
                onPageChange={setTournamentAdminPage}
                onPageSizeChange={setTournamentAdminPageSize}
                searchTerm={tournamentAdminSearch}
                onSearchChange={setTournamentAdminSearch}
                searchPlaceholder="Search tournament admins"
              />

              <AssignRoleModal
                isOpen={isAssignAdminModalOpen}
                onClose={() => setIsAssignAdminModalOpen(false)}
                token={token}
                entityType="organization"
                entityId={selectedOrganizationId || userOrganizationId}
                role={assignAdminRole}
                scope={{ organizationId: selectedOrganizationId || userOrganizationId }}
                onAssigned={(assignmentResponse) => {
                  if (assignmentResponse?.userId) {
                    setTournamentAdmins((prev) => normalizeAdminsRows(
                      prev.map((admin) => {
                        const adminId = admin.id || admin._id;
                        if (!idsMatch(adminId, assignmentResponse.userId)) return admin;
                        return {
                          ...admin,
                          roles: assignmentResponse.roles || admin.roles,
                          community: assignmentResponse.community ?? admin.community,
                          organization: assignmentResponse.organization ?? admin.organization,
                          club: assignmentResponse.club ?? admin.club,
                        };
                      })
                    ));
                  }
                  setTournamentLoadError('');
                  fetchTournamentAdmins(token, selectedOrganizationId || userOrganizationId, { includeUsers: true })
                    .then((response) => {
                      setTournamentAdmins(normalizeAdminsRows(response.admins || []));
                      setManagerCandidates(response.users || []);
                    })
                    .catch((error) => {
                      setTournamentLoadError(error.message || 'Unable to load tournaments.');
                    });
                }}
              />

              <div className="table-responsive">
                <table className="table align-middle mb-0">
                  <thead>
                    <tr>
                      <th scope="col">Admin</th>
                      <th scope="col">Email</th>
                      <th scope="col">Manager roles</th>
                      <th scope="col">Created</th>
                      <th scope="col" className="text-end">
                        <span className="visually-hidden">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {totalTournamentAdmins === 0 && (
                      <tr>
                        <td colSpan="5" className="text-muted text-center">
                          Newly created tournament admins will appear here for quick reference.
                        </td>
                      </tr>
                    )}
                    {visibleTournamentAdmins.map((admin) => {
                      const isRegisteredUser = admin.accountType === 'individual';
                      const isOrganizationAdminAccount = hasOrganizationAdminRole(admin);

                      return (
                        <tr key={admin.id || admin._id}>
                        <td className="fw-semibold">{admin.name}</td>
                        <td className="text-muted">{admin.email}</td>
                        <td className="text-muted">
                          {admin.managerRoles?.length ? admin.managerRoles.join(', ') : '—'}
                        </td>
                        <td>{admin.createdAt ? new Date(admin.createdAt).toLocaleDateString() : '—'}</td>
                        <td className="text-end">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary me-2"
                            onClick={() => handleEditTournamentAdmin(admin.id || admin._id)}
                            disabled={
                              isOrganizationAdminAccount ||
                              isRegisteredUser ||
                              editingTournamentAdminId === (admin.id || admin._id) ||
                              deletingTournamentAdminId === (admin.id || admin._id)
                            }
                            title={
                              isOrganizationAdminAccount
                                ? 'Organization admins cannot be edited here.'
                                : isRegisteredUser
                                ? 'Registered users cannot be edited here.'
                                : 'Edit tournament admin'
                            }
                          >
                            {editingTournamentAdminId === (admin.id || admin._id) ? 'Updating...' : 'Edit'}
                          </button>
                          {!isOrganizationAdminAccount && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() =>
                                handleDeleteTournamentAdmin(
                                  admin.id || admin._id,
                                  admin.managerRoles?.includes('Department Admin')
                                    || admin.roles?.includes('department_admin')
                                    || admin.roles?.includes('team_admin')
                                    ? 'department-admin'
                                    : 'tournament-admin',
                                )
                              }
                              disabled={
                                isRegisteredUser ||
                                deletingTournamentAdminId === (admin.id || admin._id) ||
                                editingTournamentAdminId === (admin.id || admin._id)
                              }
                              title={
                                isRegisteredUser
                                  ? 'Registered users cannot be removed.'
                                  : 'Remove tournament admin'
                              }
                            >
                              {deletingTournamentAdminId === (admin.id || admin._id) ? 'Removing...' : 'Remove'}
                            </button>
                          )}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {(isTournamentAdmin || isSuperAdmin) && !isOrganizationAdmin && (
          <div className="card border-0 shadow-sm">
            <div className="card-body d-grid gap-3">
              <div className="d-flex flex-column flex-sm-row align-items-sm-center justify-content-sm-between">
                <div>
                  <p className="eyebrow text-primary mb-1">Tournaments</p>
                  <h2 className="h5 mb-2">Your tournaments</h2>
                  <p className="text-muted mb-0">
                    View the tournaments you are assigned to manage and the admins supporting them.
                  </p>
                </div>
                <span className="badge bg-primary-subtle text-primary-emphasis">Tournament admin view</span>
              </div>

              {tournamentLoadError && (
                <div className="alert alert-danger py-2 mb-0">{tournamentLoadError}</div>
              )}

              <TableControls
                sortKey={assignmentSortKey}
                sortDirection={assignmentSortDirection}
                onSortKeyChange={setAssignmentSortKey}
                onSortDirectionChange={(direction) =>
                  direction ? setAssignmentSortDirection(direction) : toggleAssignmentDirection()
                }
                sortOptions={[
                  { value: 'name', label: 'Name' },
                  { value: 'admins', label: 'Admin count' },
                  { value: 'status', label: 'Status' },
                ]}
                page={assignmentPage}
                totalPages={assignmentPages}
                totalItems={totalTournamentAssignments}
                pageSize={assignmentPageSize}
                onPageChange={setAssignmentPage}
                onPageSizeChange={setAssignmentPageSize}
                searchTerm={assignmentSearch}
                onSearchChange={setAssignmentSearch}
                searchPlaceholder="Search tournaments or admins"
              />

              <div className="table-responsive">
                <table className="table align-middle mb-0">
                  <thead>
                    <tr>
                      <th scope="col">Tournament</th>
                      <th scope="col">Admins</th>
                      {showTournamentManagerColumns && (
                        <>
                          <th scope="col">Player manager</th>
                          <th scope="col">Auction manager</th>
                          <th scope="col">Match manager</th>
                        </>
                      )}
                      <th scope="col">Status</th>
                      <th scope="col" className="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoadingTournaments && (
                      <tr>
                        <td colSpan={tournamentTableColumnCount} className="text-center text-muted">
                          Loading tournaments...
                        </td>
                      </tr>
                    )}
                    {!isLoadingTournaments && totalTournamentAssignments === 0 && (
                      <tr>
                        <td colSpan={tournamentTableColumnCount} className="text-center text-muted">
                          No tournaments have been assigned to you yet.
                        </td>
                      </tr>
                    )}
                    {!isLoadingTournaments &&
                      visibleTournamentAssignments.map((tournament) => {
                        const tournamentId = tournament.id || tournament._id;
                        const managerSelection = tournamentManagers[tournamentId] || {};

                          return (
                            <tr key={tournamentId}>
                              <td className="fw-semibold">{tournament.name}</td>
                              <td>
                                {(tournament.admins || []).length === 0 ? (
                                <span className="text-muted">No admins assigned</span>
                              ) : (
                                <ul className="list-unstyled mb-0">
                                  {tournament.admins.map((admin) => (
                                    <li key={admin.id} className="d-flex flex-column">
                                      <span className="fw-semibold">{admin.name}</span>
                                      <span className="text-muted small">
                                        {admin.email} · {formatAdminRole(admin.role)}
                                      </span>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </td>
                            {showTournamentManagerColumns && (
                              <>
                                <td>
                                  <select
                                    className="form-select"
                                    value={managerSelection.registrationManagerId || ''}
                                    onChange={(event) =>
                                      handleTournamentManagerChange(
                                        tournamentId,
                                        'registrationManagerId',
                                        event.target.value,
                                      )
                                    }
                                  disabled={!tournament.isActive || updatingTournamentId === tournamentId}
                                >
                                  <option value="">No manager selected</option>
                                  {getManagerOptions(
                                    ['player_manager', 'tournament_manager'],
                                    managerSelection.registrationManagerId,
                                  ).map((admin) => (
                                    <option key={admin.id} value={admin.id}>
                                      {admin.name} ({admin.email})
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td>
                                  <select
                                    className="form-select"
                                    value={managerSelection.auctionManagerId || ''}
                                    onChange={(event) =>
                                      handleTournamentManagerChange(
                                        tournamentId,
                                        'auctionManagerId',
                                        event.target.value,
                                      )
                                    }
                                  disabled={!tournament.isActive || updatingTournamentId === tournamentId}
                                >
                                  <option value="">No manager selected</option>
                                  {getManagerOptions(
                                    ['auction_manager', 'tournament_manager'],
                                    managerSelection.auctionManagerId,
                                  ).map((admin) => (
                                    <option key={admin.id} value={admin.id}>
                                      {admin.name} ({admin.email})
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td>
                                  <select
                                    className="form-select"
                                    value={managerSelection.matchManagerId || ''}
                                    onChange={(event) =>
                                      handleTournamentManagerChange(
                                        tournamentId,
                                        'matchManagerId',
                                        event.target.value,
                                      )
                                    }
                                  disabled={!tournament.isActive || updatingTournamentId === tournamentId}
                                >
                                  <option value="">No manager selected</option>
                                  {getManagerOptions(
                                    ['match_manager', 'tournament_manager'],
                                    managerSelection.matchManagerId,
                                  ).map((admin) => (
                                    <option key={admin.id} value={admin.id}>
                                      {admin.name} ({admin.email})
                                    </option>
                                  ))}
                                </select>
                                </td>
                              </>
                            )}
                            <td>
                              <span
                                className={`badge ${
                                  tournament.isActive
                                    ? 'bg-success-subtle text-success-emphasis'
                                    : 'bg-secondary-subtle text-secondary-emphasis'
                                }`}
                              >
                                {tournament.isActive ? 'Active' : 'Archived'}
                              </span>
                            </td>
                            <td className="text-end">
                              <button
                                type="button"
                                className="btn btn-outline-primary btn-sm"
                                onClick={() => handleSaveTournamentAdmins(tournamentId)}
                                disabled={updatingTournamentId === tournamentId || !tournament.isActive}
                              >
                                {updatingTournamentId === tournamentId
                                  ? 'Saving...'
                                  : showTournamentManagerColumns
                                    ? 'Save managers'
                                    : 'Save admins'}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}


      </div>
    </div>

      {deleteModal.type && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          role="dialog"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
        >
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{deleteModal.title || 'Confirm delete'}</h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={closeDeleteModal}
                  disabled={isDeleteActionInProgress}
                />
              </div>

              <div className="modal-body">
                <p className="mb-0">
                  {deleteModal.message} This action cannot be undone.
                </p>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={closeDeleteModal}
                  disabled={isDeleteActionInProgress}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={confirmDeleteModal}
                  disabled={isDeleteActionInProgress}
                >
                  {deleteModalConfirmLabel}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isEditModalOpen && (
        <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editModal.type === 'teamAdmin' && 'Edit department admin'}
                  {editModal.type === 'tournamentAdmin' && 'Edit tournament admin'}
                  {editModal.type === 'tournament' && 'Edit tournament'}
                </h5>
                <button type="button" className="btn-close" aria-label="Close" onClick={closeEditModal} disabled={isSavingModal} />
              </div>

              <form onSubmit={handleEditModalSubmit}>
                <div className="modal-body d-grid gap-3">
                  {editModalError && <div className="alert alert-danger py-2 mb-0">{editModalError}</div>}

                  <div>
                    <label className="form-label" htmlFor="editName">
                      Name
                    </label>
                    <input
                      id="editName"
                      type="text"
                      className="form-control"
                      value={editModal.name}
                      onChange={(event) => setEditModal((prev) => ({ ...prev, name: event.target.value }))}
                      required
                    />
                  </div>

                  {editModal.type !== 'tournament' && (
                    <div>
                      <label className="form-label" htmlFor="editEmail">
                        Email
                      </label>
                      <input
                        id="editEmail"
                        type="email"
                        className="form-control"
                        value={editModal.email}
                        onChange={(event) => setEditModal((prev) => ({ ...prev, email: event.target.value }))}
                        required
                      />
                    </div>
                  )}

                  {editModal.type === 'tournament' && isSuperAdmin && (
                    <p className="mb-0 text-muted small">
                      Select an organization in the tournament form before saving edits as a super admin.
                    </p>
                  )}
                </div>

                <div className="modal-footer">
                  <button type="button" className="btn btn-outline-secondary" onClick={closeEditModal} disabled={isSavingModal}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={isSavingModal}>
                    {isSavingModal ? 'Saving...' : 'Save changes'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OrganizationTeamAdminPage;
